// Save / delete / move ticket — Mongo-backed (Phase 7 migration).
// Save / delete / move ticket against the main-server `tickets` entity
// (sdk.tickets.* via EntityDispatcher). Filename kept for caller stability;
// `el.call('saveTicket', ticket)` still works.
//
// GH sync is now a server-side cron worker (server/workers/github-sync/)
// triggered by Ticket.dirty=true; no client-side trigger needed.

// isOptimisticId comes from @symbo-ls/workspace-shell, stubbed by frank's
// EXTERNAL_PACKAGES at serialization time — reached via this.call instead
// of a raw import (see TicketDetailModal.js for the full rationale;
// isOptimisticId is a cross-package call the same way, just from
// workspace-shell rather than workspace-shared). Every caller of
// _isUnsavedId below is a `this`-bound function (el.call('saveTicket', …)
// etc.), so `this` threads straight through.
import { normalizeTicketDoc } from './ticketLoaders.js'

const TICKETS_ENTITY = 'tickets'

const _logErr = (scope, err) => console.error(`[${scope}]`, err?.message || err)

// A Mongo ObjectId is a 24-char hex string — isOptimisticId() used to return
// true for any non-number, mis-classifying real Mongo docs as optimistic
// (#2277). Persisting functions must treat Mongo ids / SMBLS-N slugs as REAL.
//
// WS-ROWOPS-OPTIMISTIC-ID-STRING-1 re-evaluation: the base classifier in
// @symbo-ls/workspace-shell/functions/rowOps.js is now correct on its own
// (only an absent id, or a Date.now()-band numeric/numeric-string id, is
// optimistic), so these two short-circuits are REDUNDANT, not load-bearing —
// saveTicket.test.js now delegates its `isOptimisticId` mock to the real
// export to keep proving that. They stay as a local, dependency-free guard:
// this package is also frank-served, where `@symbo-ls/workspace-shell` is
// replaced by an EXTERNAL_PACKAGES stub and `ctx.call('isOptimisticId', …)`
// can resolve to nothing at all.
const _isMongoId = (id) => typeof id === 'string' && /^[0-9a-f]{24}$/i.test(id)
const _isSlug = (id) => typeof id === 'string' && /^[A-Z][\w-]+-\d+$/i.test(id)
// True only for genuinely optimistic (not-yet-created) ids. `ctx` is the
// caller's `this` (an element) — needed to reach isOptimisticId via .call.
const _isUnsavedId = (ctx, id) => !_isMongoId(id) && !_isSlug(id) && ctx.call('isOptimisticId', id)

const _toPayload = (t, currentUserEmail, isUpdate = false) => {
  const prevMeta = t.metadata && typeof t.metadata === 'object' ? t.metadata : {}
  const metadata = { ...prevMeta }
  if (t.agentTest !== undefined) metadata.agent_test = !!t.agentTest
  // Deployment env (development/staging/production) — stored in metadata so it
  // persists through the existing Mixed `metadata` blob with no server schema
  // change. _mongoToTicket surfaces it back as `env`. (2026-06-15 per Nika —
  // match the project-level env model and wire it to tickets.)
  if (t.env !== undefined) metadata.env = t.env || null
  // Form-collected fields that are NOT first-class columns on the Mongo Ticket
  // schema (storyPoints, acceptanceCriteria, stepsToReproduce, expectedResult).
  // The server create() constructor and the update() `_ALLOWED_UPDATE_FIELDS`
  // allowlist build the doc from an explicit field set and silently DROP any
  // top-level key outside it — so sending these at the top level lost them on
  // every save (create showed them optimistically then they vanished on
  // refresh; edits never persisted them at all). `metadata` is a Mixed blob
  // that IS in both the create constructor AND the update allowlist, so nesting
  // them here persists end-to-end with no server schema change — exactly the
  // pattern `env` already uses. _mongoToTicket reads them back.
  // NOTE: issueType is NOT in this group — it IS the first-class `type` column
  // and is mapped onto payload.type below (#88).
  if (t.storyPoints !== undefined) metadata.storyPoints = t.storyPoints || null
  if (t.acceptanceCriteria !== undefined) metadata.acceptanceCriteria = t.acceptanceCriteria || null
  if (t.severity !== undefined) metadata.severity = t.severity || null
  // Repro steps were tried as a first-class root `reproSteps` column, but the
  // server returned it null — it is NOT in the create constructor / update
  // allowlist, so the top-level key was silently dropped. Store under
  // metadata.reproSteps (Mixed blob, always persists) like the siblings. (#88)
  if (t.stepsToReproduce !== undefined) metadata.reproSteps = t.stepsToReproduce || null
  if (t.expectedResult !== undefined) metadata.expectedResult = t.expectedResult || null
  const payload = {
    title: t.title || '(no title)',
    body: t.body || null,
    columnKey: t.columnKey || 'backlog',
    position: t.position || 0,
    assigneeEmail: t.assigneeEmail || null,
    reporterEmail: t.reporterEmail || currentUserEmail,
    labels: t.labels || [],
    priority: t.priority || null,
    dueDate: t.dueDate || null,
    refs: {
      ...(t.refs || {}),
      github: {
        ...(t.refs?.github || {}),
        issueUrl: t.githubUrl || t.refs?.github?.issueUrl || null,
        nodeId: t.githubNodeId || t.refs?.github?.nodeId || null
      }
    },
    metadata,
    dirty: true
  }
  // `state` is a PROTECTED generic-update field server-side
  // (TicketUpdateProtection.PROTECTED_GENERIC_UPDATE_FIELDS) — sending it on a
  // PUT returns HTTP 400 `protected_ticket_update`, which made EVERY edit-save
  // of an existing ticket fail (the edit `updated` object always carries the
  // ticket's current `state`). State transitions flow through the dedicated CTA
  // path (moveTicket / orchestration), so a generic save must NOT send it. On
  // CREATE the server accepts a trusted seed state (not_started / awaiting_-
  // approval / blocked) for TriageQuickCreate-style flows, so keep it there.
  if (!isUpdate && t.state) payload.state = t.state
  // Issue kind is the first-class Mongo `type` column. The form draft calls it
  // `issueType` (bug/feature/user-story/subtask/…); map it onto root `type` so
  // the board TYPE filter (`f.type = filters.issueType`) and the epic/story
  // exclusion (`f.type: { $nin: [...] }`) actually match — nesting it in
  // metadata.issueType left `type` null and neither server query ever saw it
  // (#88). The form field wins when set; fall back to a pre-existing `t.type`.
  const issueKind = t.issueType || t.type
  if (issueKind) payload.type = issueKind
  // env top-level too (forward-compat): harmless today (server drops unknown
  // keys) and persists automatically if a first-class `env` column ever lands.
  // The canonical persisted copy is metadata.env (set above).
  if (t.env !== undefined) payload.env = t.env || null
  // Epic is editable from the ticket detail sidebar. The first-class Mongo
  // column is `epic` (the server create constructor reads `payload.epic` and
  // `epic` is in the update allowlist) — the form draft calls it `epicName`,
  // so map it onto `epic` so the chosen epic actually persists. Keep `epicName`
  // too for any reader that expects the camel alias.
  if (t.epicName !== undefined) {
    payload.epic = t.epicName || null
    payload.epicName = t.epicName || null
  }
  return payload
}

// Detects no_active_organization from the error object returned by the SDK
// adapter (_settle wraps thrown Errors; the raw JSON body is in .cause).
const _isNoActiveOrg = (err) => {
  if (!err) return false
  if (err?.error === 'no_active_organization') return true
  if (err?.cause?.error === 'no_active_organization') return true
  const msg = err?.message || ''
  if (msg.includes('no_active_organization')) return true
  // Also treat HTTP 401 as a recoverable auth error — the token may be
  // stale or the active-org may not be set on the SDK session yet (#2204).
  if (err?.status === 401 || err?.statusCode === 401) return true
  if (typeof err?.cause?.status === 'number' && err.cause.status === 401) return true
  return false
}

// If the server returned no_active_organization, try to set the active org
// from the SDK's cached user data or root state and return true so the
// caller can retry.
const _tryRecoverActiveOrg = async (el) => {
  try {
    const sdk = el?.sdk || el?.context?.methods?.sdk
    if (!sdk) return false
    const me = sdk._currentUser || null
    // Prefer cached user's activeOrganization, then orgs[0], then root state
    const rootOrgId = el?.state?.root?.org?.id || null
    const orgId = me?.activeOrganization || me?.orgs?.[0]?.id || rootOrgId || null
    if (!orgId) return false
    await sdk.setActiveOrganization(orgId)
    return true
  } catch (_) {
    return false
  }
}

// Reconcile ONE ticket's local rows with the server's response after a
// write that could have changed `columnKey`.
//
// Root cause this closes: `state` and `columnKey` are two representations
// of the same workflow status. `state` is a PROTECTED field (see the
// payload comment above) — the CLIENT never sends it and never computes
// it. The SERVER derives it from the new `columnKey` on every update
// (TicketService.update's COLUMN_TO_STATE_DEFAULT / TicketStateMachine
// path) and returns the full updated doc. Every optimistic local patch
// (kanban drag, modal Status-select save, AI apply-updates) only ever
// touches `columnKey` + `position` — `state` was never written back
// anywhere, so it stayed stale forever (until a full board reload
// happened to re-fetch that exact row, which is not guaranteed once a
// page is loaded). That produced three symptoms from ONE cause: the
// moved card's status badge (state-driven) never updated, a realtime
// peer never saw the status change either (same stale field), and
// opening the SAME ticket's read view (state-driven) vs edit view
// (columnKey-driven) could disagree.
//
// Fix: `moveTicket` and `saveTicket`'s update branch are the ONLY two
// places a `columnKey` change reaches the server — both funnel their
// response through this ONE function, so every caller (drag handlers,
// the modal's Save button, AI apply-updates, quick status actions) gets
// the server's authoritative fields back with zero call-site changes.
// `root.tickets` and `root.selectedTicket` are the surfaces that render a
// per-ticket status badge from `state`; `root.myTickets` is My View's own
// cache of the same rows. All three are patched in ONE root.update() so
// they can never render three different answers for the same ticket.
const _applyServerTicketPatch = (root, ticketId, serverDoc) => {
  if (!root || ticketId == null || !serverDoc || typeof serverDoc !== 'object') return
  const normalized = normalizeTicketDoc(serverDoc, true)
  if (!normalized || normalized.id == null) return
  const idStr = String(ticketId)
  const patch = {}
  // OUTSIDE A COLUMN ⟺ DRAFT: the session-live placement map is a root
  // sibling of `tickets`, so a wholesale `metadata` replace below never
  // touched it — a stale entry survived a move into a column made by ANY
  // other writer (a second tab, an agent, "Start on") and kept winning the
  // ticketCanvasPos lookup, so the card floated on with a real column until
  // reload. The server's answer is authoritative for both halves: mirror it.
  const live = root._ticketCanvasPos
  if (live && typeof live === 'object') {
    const serverPos = normalized.metadata && normalized.metadata.canvasPos
    const isDraft = normalized.columnKey === _DRAFT
    const wantEntry = isDraft && serverPos ? serverPos : null
    const haveEntry = live[idStr] || null
    if (!!wantEntry !== !!haveEntry || (wantEntry && haveEntry !== wantEntry)) {
      const next = { ...live }
      if (wantEntry) next[idStr] = wantEntry
      else delete next[idStr]
      patch._ticketCanvasPos = next
    }
  }
  const tickets = root.tickets
  if (Array.isArray(tickets) && tickets.some((t) => t && String(t.id) === idStr)) {
    patch.tickets = tickets.map((t) => (t && String(t.id) === idStr ? { ...t, ...normalized } : t))
  }
  const sel = root.selectedTicket
  if (sel && String(sel.id) === idStr) {
    patch.selectedTicket = { ...sel, ...normalized }
  }
  const myTickets = root.myTickets
  if (Array.isArray(myTickets) && myTickets.some((t) => t && String(t.id) === idStr)) {
    patch.myTickets = myTickets.map((t) =>
      t && String(t.id) === idStr ? { ...t, ...normalized } : t
    )
  }
  if (Object.keys(patch).length) root.update(patch)
}

// Public alias (2026-08-02 per Nika "make sure tickets page is fully chained
// on socket realtime") — the write-reconciliation path above and a realtime
// UPDATE event need to do the EXACT same thing: merge a raw server ticket
// doc into whichever of tickets/selectedTicket/myTickets currently hold that
// id. board.js's realtime handler reuses this instead of hand-rolling a
// second "find by id, merge" — which also means a ticket updated by another
// tab/agent now refreshes the DETAIL MODAL if it's the one open, not just
// the board card (the modal reads root.selectedTicket, a separate slot the
// board's own tickets-array patch never touched before).
export const applyServerTicketPatch = _applyServerTicketPatch

export const saveTicket = async function (ticket) {
  const fetch = this.context?.fetch
  if (!fetch?.insert || !fetch?.update) {
    _logErr('saveTicket', 'context.fetch missing insert/update')
    return null
  }
  // Decide insert-vs-update UP FRONT (same guard used below) so the payload can
  // be shaped per-op: an UPDATE must omit the protected `state` field, an INSERT
  // may carry a trusted seed state. (#bug-edit-fails — sending state on PUT 400s.)
  const _isUpdateOp = !(
    !_isMongoId(ticket.id) &&
    !_isSlug(ticket.id) &&
    (this.call('isOptimisticId', ticket.id) || !ticket.ticketId)
  )
  const payload = _toPayload(ticket, this.state?.root?.currentUserEmail, _isUpdateOp)
  const _doInsert = () => fetch.insert({ from: TICKETS_ENTITY, data: payload })
  // _packUpdate extracts `id` from params (not `ticketId`) to route the SDK
  // EntityDispatcher's argMaps.idPayload correctly (#2277 / #2273 — updates
  // were silently no-op'd because `_packUpdate` looked for `params.id` but
  // only `params.ticketId` was set; `TicketService.update(undefined, …)` → 404).
  // Pass both keys so any argMap that reads either field works.
  const _updateId = ticket.ticketId || ticket.id
  const _doUpdate = () =>
    fetch.update({
      from: TICKETS_ENTITY,
      params: { id: _updateId, ticketId: _updateId },
      data: payload
    })
  // _isMongoId / _isSlug guards hoisted to module scope (shared with
  // moveTicket + setTicketCanvasPos, which had the same #2277-family bug).
  try {
    if (!_isUpdateOp) {
      let result = await _doInsert()
      // Auto-recover: if the first attempt fails with no_active_organization,
      // call setActiveOrganization and retry once. This handles users whose
      // User.activeOrganization was null at boot (common for new accounts or
      // accounts whose active-org got cleared by an org removal).
      if (_isNoActiveOrg(result?.error)) {
        const recovered = await _tryRecoverActiveOrg(this)
        if (recovered) result = await _doInsert()
      }
      if (result?.error) {
        _logErr('saveTicket:insert', result.error)
        return null
      }
      // Land the server's authoritative doc onto the OPTIMISTIC row, the same
      // way the update branch and every realtime event do. The insert branch
      // used to skip this, so the client kept its own guess for every
      // server-derived field — most visibly `columnKey` and `state`. A create
      // in Todo that the server stored elsewhere left a card sitting in a
      // column no record backs, and the realtime echo of the real record then
      // rendered a SECOND card (PLATFORM-TICKET-CREATE-COLUMN-DUPLICATE-1).
      // Keyed by the TEMP id — the patch swaps in the server's id too, so the
      // caller's own temp→real swap afterwards is a harmless no-op.
      _applyServerTicketPatch(this.state?.root, ticket.id, result?.data)
      // Prefer the Mongo ObjectId (_id) as the realId so subsequent edits
      // pass the ID guard (_isMongoId) correctly and never re-INSERT (#2277).
      return result?.data?._id ?? result?.data?.ticketId ?? null
    }
    let result = await _doUpdate()
    if (_isNoActiveOrg(result?.error)) {
      const recovered = await _tryRecoverActiveOrg(this)
      if (recovered) result = await _doUpdate()
    }
    if (result?.error) {
      _logErr('saveTicket:update', result.error)
      return null
    }
    // Land the server's authoritative doc (state derived from the new
    // columnKey when it changed) back into local state — see
    // _applyServerTicketPatch. Return contract is unchanged (still the id)
    // so no caller needs to change.
    _applyServerTicketPatch(this.state?.root, _updateId, result?.data)
    return ticket.ticketId || ticket.id
  } catch (e) {
    _logErr('saveTicket', e)
    return null
  }
}

// `opts.callerReports`: a caller that tells the person itself (the AI
// timeline's Undo row, aiUndoCreated) gets the failure THROWN, with the SDK
// error's `status` / `code` intact, so it can say why in the person's
// language. It logs the error too. Every other caller keeps the `false`.
//
// The DELETE that deletes the ticket answers 204. A DELETE that finds it
// already deleted changes nothing and answers 200 `{ alreadyDeleted: true }`
// (server contract B, 2026-10-31). The reporting caller gets that answer as
// a 410: the ticket is gone, this delete did nothing. CHECK 8d (dev server
// 475a31202, 2026-10-31): the AI's delete_ticket deleted "Undo gone check",
// the row's Undo sent the DELETE again, got a 204 and said "undone" for a
// delete that changed nothing. deleteEvent throws a 404 for its refused soft
// delete in the same way. Every other caller reads any 2xx as deleted and
// keeps the `true`.
export const deleteTicket = async function (ticketId, opts) {
  const callerReports = !!(opts && opts.callerReports)
  const fetch = this.context?.fetch
  if (!fetch?.delete) {
    if (callerReports) {
      throw Object.assign(new Error('context.fetch.delete missing'), {
        code: 'DELETE_UNAVAILABLE'
      })
    }
    _logErr('deleteTicket', 'context.fetch.delete missing')
    return false
  }
  let result
  try {
    // The sdk adapter's _packDelete extracts params.id (not params.ticketId),
    // so pass id here so the EntityDispatcher receives the correct identifier
    // (#2266 — delete silently no-op'd because ticketId was never forwarded).
    result = await fetch.delete({
      from: TICKETS_ENTITY,
      params: { id: ticketId, ticketId }
    })
  } catch (e) {
    if (callerReports) throw e
    _logErr('deleteTicket', e)
    return false
  }
  if (result?.error && callerReports) throw result.error
  if (callerReports && result?.data?.alreadyDeleted === true) {
    throw Object.assign(new Error(`ticket ${ticketId} was already deleted`), {
      status: 410
    })
  }
  return !result?.error
}

// Persist a freeform canvas placement (#32 — Tickets "Card placement:
// Freeform"). Stored under metadata.canvasPos ({x,y} in board-scene
// coordinates) so it survives reloads; null clears it (card returned to a
// column). Sends the FULL merged metadata — the server replaces the object.
export const setTicketCanvasPos = async function (ticketId, pos) {
  const fetch = this.context?.fetch
  if (!fetch?.update) {
    _logErr('setTicketCanvasPos', 'context.fetch.update missing')
    return false
  }
  // Skip ONLY genuinely-unsaved optimistic ids — raw isOptimisticId() is true
  // for Mongo hex ids too, which silently skipped persistence for EVERY real
  // ticket (freeform placements never reached the server).
  if (_isUnsavedId(this, ticketId)) return true
  try {
    const root = this.state?.root
    const ticket = (root?.tickets || []).find((t) => t.id === ticketId)
    const metadata = { ...(ticket?.metadata || {}), canvasPos: pos || null }
    const result = await fetch.update({
      from: TICKETS_ENTITY,
      params: { id: ticketId, ticketId },
      data: { metadata }
    })
    if (result?.error) {
      _logErr('setTicketCanvasPos', result.error)
      return false
    }
    return true
  } catch (e) {
    _logErr('setTicketCanvasPos', e)
    return false
  }
}

// Mirrors ticketUtils.js DRAFT_COLUMN_KEY. A local literal, not an import:
// this file is frank-served and must stay dependency-free (see _isUnsavedId).
const _DRAFT = 'draft'

// Move ticket between columns / reorder. SENDS columnKey + position, and —
// OUTSIDE A COLUMN ⟺ DRAFT (2026-09-02 per Nika) — the placement half of the
// same move IN THE SAME REQUEST:
//   • into a real column → `metadata.canvasPos: null` (merge mode deletes the
//     key), so the seven column-entry paths that never cleared a position
//     (modal Status, "Start on", "Ready to review", My View drops, …) now do,
//     for free;
//   • into `draft` with a `canvasPos` → the position travels with the column.
// One request instead of two unawaited ones racing on the same document: the
// old pair could half-land (measured on dev — the column write refused, the
// position write kept — leaving a `todo` card floating outside every column
// that the Drafts filter could not hide). The server enforces the same rule
// on every writer; this is the client keeping its own two halves honest.
// `state` stays a protected, server-derived field (see _applyServerTicketPatch).
// server-side dirty=true triggers the github-sync worker.
export const moveTicket = async function ({ ticketId, columnKey, position, canvasPos }) {
  const fetch = this.context?.fetch
  if (!fetch?.update) {
    _logErr('moveTicket', 'context.fetch.update missing')
    return false
  }
  // Same #2277-family guard as setTicketCanvasPos — raw isOptimisticId()
  // skipped persistence for Mongo hex ids, so column drags never saved.
  if (_isUnsavedId(this, ticketId)) return true
  try {
    const data = { columnKey, position }
    if (columnKey === _DRAFT) {
      if (canvasPos) data.metadata = { canvasPos }
    } else {
      data.metadata = { canvasPos: null }
    }
    // Pass both id and ticketId so _packUpdate can extract params.id correctly
    // (#2273 — moveTicket silently no-op'd because _packUpdate reads params.id,
    // not params.ticketId, same root cause as _doUpdate fix above).
    const result = await fetch.update({
      from: TICKETS_ENTITY,
      params: { id: ticketId, ticketId },
      data
    })
    if (result?.error) {
      _logErr('moveTicket', result.error)
      return false
    }
    // The response carries the server-derived `state` for the new column —
    // land it (see _applyServerTicketPatch). Every caller (kanban drag,
    // AI apply-updates) gets this for free; none of them need to change.
    _applyServerTicketPatch(this.state?.root, ticketId, result?.data)
    return true
  } catch (e) {
    _logErr('moveTicket', e)
    return false
  }
}
